import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Check, X, Edit3, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const EmailPanel = ({ lead, onChange, onStatus }) => {
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState(lead.email_to || lead.email);
  const [subject, setSubject] = useState(lead.email_subject);
  const [body, setBody] = useState(lead.email_body);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.updateEmail(lead.id, {
        email_to: to,
        email_subject: subject,
        email_body: body,
      });
      onChange?.(updated);
      setEditing(false);
      toast.success("E-post lagret");
    } catch (e) {
      toast.error("Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="email-preview-panel"
      className="mt-3 rounded-xl border border-border bg-secondary/50 p-4 space-y-3"
    >
      <div className="flex items-center gap-2 text-xs font-mono-brand uppercase tracking-wider text-primary">
        <Mail className="h-3.5 w-3.5" /> Utkast e-post
      </div>

      <div className="space-y-2">
        <div>
          <Label className="text-xs text-muted-foreground">Til</Label>
          <Input
            data-testid="email-to-input"
            value={to}
            disabled={!editing}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 text-sm bg-card"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Emne</Label>
          <Input
            data-testid="email-subject-input"
            value={subject}
            disabled={!editing}
            onChange={(e) => setSubject(e.target.value)}
            className="h-9 text-sm bg-card"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Melding</Label>
          <Textarea
            data-testid="email-body-textarea"
            value={body}
            disabled={!editing}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="text-sm bg-card leading-relaxed resize-none scroll-thin"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        {editing ? (
          <Button
            data-testid="email-edit-toggle-button"
            size="sm"
            onClick={save}
            disabled={saving}
            className="gap-1.5"
          >
            <Save className="h-4 w-4" /> LAGRE
          </Button>
        ) : (
          <Button
            data-testid="email-edit-toggle-button"
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="gap-1.5"
          >
            <Edit3 className="h-4 w-4" /> REDIGER
          </Button>
        )}
        <Button
          data-testid="email-approve-button"
          size="sm"
          onClick={() => onStatus(lead.id, "godkjent")}
          className="gap-1.5 bg-[#16A34A] hover:bg-[#15803d] text-white"
        >
          <Check className="h-4 w-4" /> GODKJENN
        </Button>
        <Button
          data-testid="email-reject-button"
          size="sm"
          variant="outline"
          onClick={() => onStatus(lead.id, "avvist")}
          className="gap-1.5 text-destructive border-destructive/40 hover:bg-destructive/10"
        >
          <X className="h-4 w-4" /> AVVIS
        </Button>
      </div>
    </div>
  );
};
